import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { SEO } from "@/components/SEO";
import { StructuredData } from "@/components/StructuredData";
import { Hero } from "@/components/site/Hero";
import { ClientLogos } from "@/components/site/ClientLogos";
import { ProblemSolution } from "@/components/site/ProblemSolution";
import { HowItWorksVisual } from "@/components/site/HowItWorksVisual";
import { RoiCalculator } from "@/components/site/RoiCalculator";
import { Comparison } from "@/components/site/Comparison";
import { CtaBand } from "@/components/site/CtaBand";
import { SiteLayout } from "@/components/site/SiteLayout";
import { baseSiteSchemaGraph, videoObjectSchema } from "@/lib/seoSchema";
import {
  buildEmbeddedAppPath,
  isEmbeddedShopifyLaunch,
  openShopifyTopLevel,
  setShopifyEmbeddedContext,
} from "@/lib/shopifyEmbedded";

function Index() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    const shop = searchParams.get("shop");
    const host = searchParams.get("host");
    const embedded = searchParams.get("embedded");
    const isEmbeddedLaunch = isEmbeddedShopifyLaunch(searchParams);

    if (shop && shop.includes(".myshopify.com")) {
      setShopifyEmbeddedContext({
        embedded: embedded === "1" || Boolean(host),
        host,
        shop,
      });

      if (isEmbeddedLaunch) {
        if (authLoading) {
          return;
        }

        const params = new URLSearchParams();
        params.set("platform", "shopify");
        // A fresh embedded launch only has Shopify's App Bridge session id.
        // The signup tab is valid after OAuth has created our pending install
        // session; otherwise show login until that install flow is completed.
        params.set("tab", "login");
        params.set("shop", shop);
        if (host) params.set("host", host);
        if (embedded === "1" || host) params.set("embedded", "1");
        const idToken = searchParams.get("id_token");
        if (idToken) params.set("id_token", idToken);

        const targetPath = user ? "/merchant-dashboard" : "/merchant-login";
        navigate(buildEmbeddedAppPath(targetPath, params), { replace: true });
        return;
      }

      const oauthUrl = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-oauth-init`);
      oauthUrl.searchParams.set("shop", shop);
      if (host) oauthUrl.searchParams.set("host", host);
      openShopifyTopLevel(oauthUrl.toString());
    }
  }, [authLoading, navigate, searchParams, user]);

  return (
    <SiteLayout>
      <SEO
        title="Splitkart: Group Gifting & Split Payments for Shopify"
        description="Make group gifting easy with Splitkart. Let customers split high-ticket orders at checkout to boost AOV and reach new shoppers instantly."
        url="/"
      />
      <StructuredData data={[baseSiteSchemaGraph(), videoObjectSchema()]} />
      <Hero />
      <ClientLogos />
      <ProblemSolution />
      <HowItWorksVisual />
      <RoiCalculator />
      <Comparison />
      <CtaBand />
    </SiteLayout>
  );
}

export default Index;
